import { useEffect, useState } from "react";
import { createRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { cn, displayName } from "@/lib/utils";
import {
  Bell,
  ChevronRight,
  ClipboardList,
  CreditCard,
  LogOut,
  MapPin,
  Monitor,
  Package,
  PackageCheck,
  LayoutGrid,
  PackagePlus,
  PackageSearch,
  Settings,
  ShieldCheck,
  Store,
  Tag,
  Ticket,
  Truck,
  Users,
  Wallet,
  QrCode,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { rootRoute } from "./root.tsx";
import { useIsDesktop } from "../lib/viewport.ts";
import { DesktopMe } from "./desktop/me.tsx";
import { TabPage } from "../components/layout.tsx";
import { Badge, Button, Empty, Price, SectionTitle, Spinner, fen } from "../components/ui.tsx";
import { Card } from "@/components/ui/card";
import { api } from "../api/client.ts";
import { useIdentities, useLogout, useMe } from "../api/queries.ts";
import { readIdentity, writeIdentity, writePurchaseAs } from "../lib/identity.ts";

/**
 * 「我的」。
 *
 * 🔴 **订单状态要做成四个快捷入口，而不是一行「我的订单」。**
 *    用户来这一页十次有八次是想看"我那单发了没" ——
 *    藏在二级页里等于每次都多点一下。
 */

/** 订单快捷入口。⚠️ 跟 `/orders` 页的筛选项一一对应，点进去直接落到那个 tab。 */
const ORDER_TABS: { key: string; label: string; icon: LucideIcon }[] = [
  { key: "pending", label: "待付款", icon: CreditCard },
  { key: "paid", label: "待发货", icon: Package },
  { key: "shipped", label: "待收货", icon: Truck },
  { key: "done", label: "已完成", icon: PackageCheck },
];

/**
 * 身份卡上给每个店铺标的那一小行字。
 *
 * 🔴 **平台标的是「我的店铺」，不是「平台」。**
 *    老板：「第二个不应该是平台，应该是我的店铺，第三个是平台管理」。
 *    陈总的三样东西是 ①我自己买东西 ②**我的店铺** ③平台管理 ——
 *    第二行标成"平台"会跟第三行糊在一起，他会以为那是同一件事的两个入口。
 *
 * ⚠️ 其他几种照旧标身份（经销商 / 分销商 / 供应商）：
 *    那是他在链条上的位置，不会跟别的东西混。
 */
const ORG_LABEL: Record<string, string> = {
  platform: "我的店铺",
  supplier: "供应商",
  dealer: "经销商",
  distributor: "分销商",
};

function MenuRow({
  to,
  search,
  icon: Icon,
  label,
  hint,
  last,
}: {
  to: string;
  search?: Record<string, unknown>;
  icon: LucideIcon;
  label: string;
  hint?: string;
  last?: boolean;
}) {
  return (
    <Link
      to={to}
      search={(search ?? {}) as never}
      className={`flex items-center gap-3 px-4 py-3.5 active:bg-secondary ${
        last ? "" : "border-b border-border"
      }`}
    >
      <Icon className="size-[18px] shrink-0 text-muted-foreground" />
      <span className="flex-1 text-[14px]">{label}</span>
      {hint && <span className="text-[12px] text-muted-foreground">{hint}</span>}
      <ChevronRight className="size-4 shrink-0 text-muted-foreground/60" />
    </Link>
  );
}


/**
 * ═══════════════════════════════════════════════════════════════
 *  身份切换
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **一个人同时是买家和经营者，但这两件事不该混在一页上。**
 *    真实业务里一个装修师傅**自己家装修时是买家、给客户配材料时是商家**，
 *    甚至同一天两种事都做。把两套内容堆在一起，页面会长到划不完 ——
 *    他在工地上单手划手机，找不到东西。
 *
 *    所以「我的」这一页按身份**整页换内容**：切到买家看订单地址，
 *    切到某家店看经营入口。切换器只有在他确实有商家身份时才出现，
 *    纯买家看不到任何多余的东西。
 *
 * ⚠️ 选择要记住。每次进来都退回买家的话，天天做生意的人每天都要多点一下。
 */
interface LevelStatus {
  current: { name: string } | null;
  stats: { totalGmv: number };
}
interface Balance {
  withdrawable: number;
}
interface ShopSelf {
  id: number;
  name: string;
  displayName: string;
  status: string;
  orderCount: number;
  since: string;
  contactPhone: string;
}

function orgGet<T>(path: string, orgId: number) {
  return fetch(`/api/merchant${path}`, {
    credentials: "include",
    headers: { "X-Org-Id": String(orgId) },
  }).then(async (r) => {
    if (!r.ok) throw new Error((await r.json().catch(() => null))?.message ?? "请求失败");
    return r.json() as Promise<T>;
  });
}

/**
 * 商家身份下「我的」页的内容。
 *
 * 🔴 **这一页只做导航，不放任何表单。**
 *    改价、提现、发券每一样都有自己的页 —— 全塞在这儿的话，
 *    他想改个电话要往下划半天，而且改错了都不知道改的是哪个。
 */
function MerchantView({ org }: { org: { id: number; name: string; type: string; status: string } }) {
  /**
   * ═══════════════════════════════════════════════════════════════
   *  能干什么，按服务端的实际放行范围来
   * ═══════════════════════════════════════════════════════════════
   *
   * 🔴 **菜单里出现一个点进去必然报错的入口，比没有这个入口更糟。**
   *    早先这里只有一个 `isReseller`（把平台也算进去），于是平台身份的菜单里
   *    挂着「收益明细」「提现」「团队与客户」「我的商品」「店铺设置」——
   *    每一个点进去都是一句红色的「这个操作只对 supplier / dealer / distributor 开放」。
   *
   * ⚠️ 对着 `apps/api/src/routes/merchant.ts` 改，别凭印象：
   *    `pricerOnly`/`inviterOnly` = platform / dealer / distributor；
   *    `resellerOnly` = dealer / distributor；
   *    `supplierOnly`/`anyMerchant` = supplier / dealer / distributor。
   *    平台的钱和店在 `/admin` 平台后台里看，不走商家端这套接口。
   */
  /** 能定价、能摆货架、能发券、能拉客户。 */
  const canPrice =
    org.type === "platform" || org.type === "dealer" || org.type === "distributor";
  /** 有下级和客户可管。 */
  const canRecruit = org.type === "dealer" || org.type === "distributor";
  /**
   * 🔴 **"能上自己的货"和"有自己的钱包"是两回事。**
   *    平台能上自营的货（服务端 `supplierOnly` 放行平台），
   *    但它的钱和店铺资料在 `/admin` 里 —— 别用同一个开关。
   */
  /**
   * 谁能上架自己的货。
   *
   * 🔴 **经销商不在里面**（老板 2026-08-31 定的）：经销商是签约渠道商，
   *    卖的是链上游供的货、靠差价吃饭，不自己组货；分销商（装修师傅那一档）
   *    会有自己进的辅料和自己做的定制柜，所以他能上架。
   *
   * ⚠️ 这份名单必须跟服务端 `merchant.ts` 的 `supplierOnly` 一字不差 ——
   *    多放一个，菜单里就会挂着一个点进去必然 403 的入口；少放一个，
   *    他明明有权限却找不到入口。
   * ⚠️ **"能上自己的货"和"有自己的钱包"是两回事，别用同一个开关**：
   *    平台能上自营的货，但它的钱和店铺资料在 `/admin` 里。
   */
  const canListProducts =
    org.type === "platform" || org.type === "supplier" || org.type === "distributor";
  /** 有自己的钱包和店铺资料。⚠️ 平台不在里面。 */
  const hasOwnShop =
    org.type === "supplier" || org.type === "dealer" || org.type === "distributor";

  /** ⚠️ `/shop` 和 `/balance` 都是 `anyMerchant` —— 平台身份查会 403，别发这两个请求。 */
  const shop = useQuery({
    queryKey: ["merchant", "shop", org.id],
    queryFn: () => orgGet<ShopSelf>("/shop", org.id),
    enabled: hasOwnShop,
  });
  const balance = useQuery({
    queryKey: ["merchant", org.id, "balance"],
    queryFn: () => orgGet<Balance>("/balance", org.id),
    enabled: hasOwnShop,
  });
  const level = useQuery({
    queryKey: ["merchant", org.id, "level"],
    queryFn: () => orgGet<LevelStatus>("/level", org.id),
    /** ⚠️ `/level` 是 `resellerOnly` —— 平台身份查会 403。 */
    enabled: canRecruit,
  });

  /**
   * 🔴 **供应商和平台不在手机上办公。**
   *    上传几十个 SKU、核对个税台账这些活手机上做不了 ——
   *    与其给他一套残缺的手机界面，不如直接说清楚去哪儿。
   */
  /**
   * 🔴 **供应商只有"后台"这一件事。**
   *    货就是他自己出的，不存在"进货"；他也没有店铺卖给消费者。
   *    上架商品、核对台账手机上做不了 —— 与其给一套残缺的界面，
   *    不如直接说清楚去哪儿。
   */
  /**
   * 🔴 **供应商在手机上要能干他每天干的那两件事：看货 / 发货。**
   *
   *    早先这里只给一句"去电脑上做" —— 但他日常最要紧的是
   *    「我卖的东西还有多少库存」和「哪几单要发」，
   *    而这两件事恰恰是站在仓库里拿着手机最该能做的。
   *    真正需要电脑的是"一次上架几十个 SKU"那种活，所以后台入口保留。
   */
  if (org.type === "supplier") {
    return (
      <>
        <Card className="-mt-6 px-4 py-4">
          <div className="flex items-center gap-3">
            <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-brand-50">
              <Store className="size-5 text-brand-600" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[15px] font-medium">{org.name}</span>
              <span className="mt-1 flex flex-wrap items-center gap-1.5">
                <Badge variant="secondary">供应商</Badge>
                {org.status === "pending" && <Badge variant="warning">待审核</Badge>}
                {org.status === "suspended" && <Badge variant="destructive">已停业</Badge>}
              </span>
            </span>
          </div>
        </Card>

        <Card className="overflow-hidden">
          <MenuRow
            to="/my-products"
            search={{ orgId: org.id }}
            icon={PackageSearch}
            label="我的商品"
            hint="供货价、库存"
          />
          {/* 🔴 发货在手机上做最顺手 —— 人就在仓库里 */}
          <MenuRow
            to="/ship"
            search={{ orgId: org.id }}
            icon={Truck}
            label="要我发的货"
            hint="填运单号"
          />
          <MenuRow
            to="/supplier"
            icon={Monitor}
            label="供应商后台"
            hint="电脑上打开"
            last
          />
        </Card>

        <p className="px-1 text-[12px] leading-relaxed text-muted-foreground">
          一次上架几十个规格、核对台账这些活在电脑上做更顺手，走上面的后台。
        </p>
      </>
    );
  }

  const days = shop.data
    ? Math.max(1, Math.round((Date.now() - new Date(shop.data.since).getTime()) / 86_400_000))
    : 0;

  return (
    <>
      {/*
        ── 店铺概览：一眼看到"我这店现在什么样" ──

        🔴 **平台老板同时有三个身份，而且互不影响：**
           ① 他有**自己的店铺**（平台店，卖给自己注册的陌生消费者）
           ② 他自己**也要买东西**（从供应商进货，拿的是供货价）
           ③ 他要**管后台**（审商品、看全站订单、改配置）
           所以这张卡上三样都在：视角切换管前两个，后台入口管第三个。
      */}
      <Card className="-mt-6 px-4 py-4">
        <div className="flex items-center gap-3">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-brand-50">
            <Store className="size-5 text-brand-600" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[15px] font-medium">
              {shop.data?.displayName || org.name}
            </span>
            <span className="mt-1 flex flex-wrap items-center gap-1.5">
              <Badge variant="secondary">{ORG_LABEL[org.type] ?? org.type}</Badge>
              {level.data?.current && <Badge variant="brand">{level.data.current.name}</Badge>}
              {org.status === "pending" && <Badge variant="warning">待审核</Badge>}
              {org.status === "suspended" && <Badge variant="destructive">已停业</Badge>}
            </span>
          </span>
        </div>

        {shop.data && (
          <p className="mt-3 text-[12px] text-muted-foreground">
            成交 {shop.data.orderCount} 单 · 开店 {days} 天
            {level.data && (
              <>
                {" · "}累计 <Price cents={level.data.stats.totalGmv} />
              </>
            )}
          </p>
        )}

      </Card>

      {/*
        🔴 **没填联系方式会直接丢单**：线下收款，买家下完单要联系你付款。
           安静地空着的话商家永远不会意识到问题出在这里，所以主动顶到最上面。
      */}
      {/*
        ⚠️ **`contactPhone?.trim()` 的问号不能省。** 一个页面因为读到形状不对的
           数据就整页白屏，代价远大于少显示一条提醒。
           （真出过：邀请页那张卡用裸 fetch 把 403 的错误体写进了同一个 queryKey，
           回到这一页 `shop.data` 是 `{code,message}`，`.trim()` 当场炸。
           根因已在那边修掉，这里是第二道。）
      */}
      {shop.data && !shop.data.contactPhone?.trim() && (
        <Link
          to="/shop-settings"
          search={{ orgId: org.id } as never}
          className="block rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-800 active:opacity-70"
        >
          还没填收款联系方式 —— 客户下单后不知道该找谁付款，这单大概率就丢了。去填 →
        </Link>
      )}

      {/*
        🔴 **一张平铺的列表，不分组。**
           商家要做的事总共就这么几件 —— 拆成"经营 / 客户 / 钱 / 设置"四段
           反而多出四行小标题，屏幕上一半是标题、一半是内容，
           一眼扫过去要先读标题再找条目。功能不多的时候，分组是负担不是帮助。
      */}
      <Card className="overflow-hidden">
        {canPrice && (
          <MenuRow to="/invite-share" icon={QrCode} label="邀请客户" hint="扫了就是你的客人" />
        )}
        {/*
          ⚠️ 这里原来有一条「进货」——已经挪走了。
             进货是**「我自己」**要做的事（我这个人买东西），不是这家店的经营动作。
             入口在顶部切到「我自己」之后那一条「去逛商城」。
        */}
        {canPrice && (
          <MenuRow
            to="/my-pricing"
            search={{ orgId: org.id }}
            icon={Tag}
            label="我的定价"
            hint="我卖多少"
          />
        )}
        {/*
          🔴 **「我的商品」和「我的定价」是两件事，不能合。**
             定价管的是**上级供的货我卖多少**；这一页管的是**我自己上架的货**
             （自己进的辅料、自己做的定制柜）——
             后者以前只有桌面版有界面，分销商在手机上一件都传不了。
        */}
        {/* ⚠️ 平台也有这一页 —— 后台那边管的是全站商品，不是"我自己上架一件"。 */}
        {canListProducts && (
          <MenuRow
            to="/my-products"
            search={{ orgId: org.id }}
            icon={PackageSearch}
            label="我的商品"
            hint="我自己上架的"
          />
        )}
        {canPrice && (
          <MenuRow
            to="/shop-categories"
            search={{ orgId: org.id }}
            icon={LayoutGrid}
            label="店铺分类"
            hint="我店里的货架"
          />
        )}
        <MenuRow
          to="/seller-orders"
          search={{ orgId: org.id }}
          icon={ClipboardList}
          label="我的订单"
          hint="客户下的单"
        />
        {/*
          ⚠️ 「卖家自己备货发货」时这些单归他发；供应商直发时这里是空的。
             具体归谁由服务端按 `shipBy` 判，前端不复制这条规则。
        */}
        <MenuRow
          to="/ship"
          search={{ orgId: org.id }}
          icon={Truck}
          label="要我发的货"
          hint="填运单号"
        />
        {canRecruit && (
          <MenuRow
            to="/team"
            search={{ orgId: org.id }}
            icon={Users}
            label="团队与客户"
            hint="下级、客户"
          />
        )}
        {canPrice && (
          <MenuRow
            to="/coupons"
            search={{ orgId: org.id }}
            icon={Ticket}
            label="优惠券"
            hint="从你的差价里出"
          />
        )}
        {/*
          ⚠️ 钱和店铺资料这几项服务端只对 supplier / dealer / distributor 放行 ——
             平台的账和店在 `/admin` 里，不走商家端这套接口。
        */}
        {hasOwnShop && (
          <>
            <MenuRow
              to="/earnings"
              search={{ orgId: org.id }}
              icon={Wallet}
              label="收益明细"
              hint="每一笔从哪来"
            />
            {/* ⚠️ 把可提现余额直接摆在入口上 —— 这是他最常想知道的一个数 */}
            <MenuRow
              to="/withdraw"
              search={{ orgId: org.id }}
              icon={CreditCard}
              label="提现"
              hint={
                balance.data ? `可提现 ${fen(balance.data.withdrawable)}` : undefined
              }
            />
            <MenuRow
              to="/shop-settings"
              search={{ orgId: org.id }}
              icon={Settings}
              label="店铺设置"
              hint="店名、联系方式、加价率"
              last={org.type !== "platform"}
            />
          </>
        )}
        {/*
          ⚠️ 「平台后台」原来在这条菜单的最后一行 —— 已经挪到顶部的身份胶囊了。
             老板：「这是三个东西，不要混在一起」：
             ①「我自己」买东西 ②「我的店」经营 ③「平台后台」管理。
        */}
      </Card>
    </>
  );
}

/** 身份切换的一个胶囊。⚠️ 点击区最小 40px —— 建材买家常戴着手套用手机。 */
function IdentityChip({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={
        "shrink-0 rounded-full px-4 py-2 text-[13px] whitespace-nowrap transition-colors " +
        (active
          ? "bg-white font-medium text-brand-700"
          : "bg-white/20 text-white/90 backdrop-blur")
      }
    >
      {label}
    </button>
  );
}

function Me() {
  const navigate = useNavigate();
  const { data: me, isPending } = useMe();
  const { data: identities } = useIdentities(Boolean(me));
  const logout = useLogout();

  /**
   * 当前身份。空串 = 买家（我自己）。
   *
   * 🔴 **所有 hook 必须排在 early return 之前。**
   *    顺手写在 `if (!me) return` 后面的话：未登录那一轮少跑几个 hook、
   *    登录后又多出来，React 直接抛 "Rendered more hooks than during the
   *    previous render" 整页白屏 —— 而生产构建里只给一个压缩过的错误编号。
   *    这个坑本项目已经踩过一次了。
   *
   * ⚠️ 用 state 存一份，切换时才会重渲染 ——
   *    只读 localStorage 的话点了没反应，用户会一直点。
   */
  const [identity, setIdentityState] = useState<string | null>(readIdentity);
  const setIdentity = (v: string) => {
    setIdentityState(v);
    writeIdentity(v);
  };
  const orgs = identities?.orgs ?? [];
  const activeOrg = orgs.find((o) => String(o.id) === identity) ?? null;

  /**
   * 记住的那个组织可能已经不在了（被清退、被移出成员）——
   * 对不上就清掉，不能让页面停在一个不存在的身份上。
   *
   * 🔴 **没选过身份的商家，默认就落在他自己的店上。**
   *    王师傅是有上级的分销商，他登录之后逛首页却看到平台指导零售价 ——
   *    那是给陌生人看的最贵一档，他这辈子都不会按那个价买。
   *    默认到买家身份，等于让他每次都要先想起来切一下。
   *    ⚠️ 只对**能进货的**身份这么做（经销商 / 分销商）：
   *       平台老板和供应商默认还是买家，他们逛商城本来就是在买东西。
   */
  /**
   * 未读消息数。
   * ⚠️ 跟 `me` 绑在一起 —— 没登录时不发这个请求（会 401）。
   */
  const unreadQ = useQuery({
    queryKey: ["messages", "unread"],
    queryFn: () => api.get<{ count: number }>("/me/messages/unread"),
    enabled: Boolean(me),
    /** ⚠️ 别设太短：这是每次打开「我的」都会跑的请求。 */
    staleTime: 30_000,
  });
  const unread = unreadQ.data?.count ?? 0;

  /** 记住的那个组织可能已经不在了（被清退、被移出成员）—— 对不上就退回「我自己」。 */
  useEffect(() => {
    if (identities && identity && !activeOrg) setIdentity("");
  }, [identity, identities, activeOrg]);

  /**
   * ═══════════════════════════════════════════════════════════════
   *  🔴 顶部这两个胶囊**本身就是两种价格视角**
   * ═══════════════════════════════════════════════════════════════
   *
   * - **「我自己」= 我这个人在进货。**
   *   首页显示的是**上一级给我的价**（我的进货价）。
   *   ⚠️ 不是平台指导价 —— 那是给**自己注册、没有上级**的陌生人看的最贵一档，
   *   一个有上级的商家永远不会按那个价买东西。
   *
   * - **「<我的店铺>」= 我在看自己的店。**
   *   首页显示的是**我卖给客户的价**，也就是客户进我店里看到的那个数。
   *
   * ⚠️ **这一页只负责"他选了谁"**，具体该按什么价看由根组件统一推导
   *    （`derivePriceView`）—— 两处各算一遍的话迟早走岔，
   *    而且登录后直接落在首页的人根本经不过这一页。
   */

  if (isPending)
    return (
      <TabPage>
        <Spinner />
      </TabPage>
    );

  if (!me) {
    return (
      <TabPage>
        <Empty
          title="还没登录"
          hint="登录后可以下单、查看订单、管理收货地址"
          action={{ label: "去登录", to: "/login" }}
        />
      </TabPage>
    );
  }

  const name = displayName(me);

  return (
    <TabPage>
      {/*
        ⚠️ 顶部用主色铺底 + 白色文字，跟首页同一个手法 ——
           两个一级页顶部长得一样，用户才觉得是同一个 App。
      */}
      <header className="bg-gradient-to-b from-brand-600 to-brand-500 px-4 pt-safe-5 pb-8">
        <div className="flex items-center gap-3 pt-4">
          <span className="flex size-14 shrink-0 items-center justify-center rounded-full bg-white/20 text-[22px] font-medium text-white backdrop-blur">
            {(activeOrg?.name ?? name).slice(0, 1)}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[18px] font-medium text-white">
              {activeOrg?.name ?? name}
            </span>
            <span className="block truncate text-[13px] text-white/75">
              {activeOrg ? "经营者身份" : (me.phone ?? me.email)}
            </span>
          </span>

          {/*
            🔴 **消息入口放在最显眼的地方，两种身份都看得到。**
               上游调价的提醒原先只在「我的定价」页顶部 —— 商家得先想起来切身份、
               再点进两层才看得到，而那条说的是"你有 N 个规格已经在亏本卖"。
            ⚠️ 未读数超过 99 显示 99+ —— 三位数会把那个小圆点撑变形。
          */}
          <Link
            to="/messages"
            className="relative flex size-11 shrink-0 items-center justify-center rounded-full bg-white/15 active:bg-white/25"
            aria-label="消息"
          >
            <Bell className="size-5 text-white" />
            {unread > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex min-w-[18px] items-center justify-center rounded-full bg-destructive px-1 text-[11px] leading-[18px] font-medium text-white">
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </Link>
        </div>

        {/*
          🔴 **只有确实有商家身份的人才看得到切换器。**
             纯买家看到"切换身份"只会困惑 —— 他没有别的身份可切。
          ⚠️ 横向可滚：一个人可能开好几家店，挤在一行里会把名字压扁。
        */}
        {orgs.length > 0 && (
          <div className="-mx-4 mt-4 flex gap-2 overflow-x-auto px-4 no-scrollbar">
            {/*
              🔴 **三个胶囊是「我是买家 / 我是卖家 / 平台管理」**（老板定的字眼）。
                 说的是**这一刻我以什么身份在用这个站**，不是"我是谁"。
            */}
            <IdentityChip
              active={!activeOrg}
              label="我是买家"
              onClick={() => setIdentity("")}
            />
            {orgs.map((o) => (
              <IdentityChip
                key={o.id}
                active={activeOrg?.id === o.id}
                /** ⚠️ 老板：「一个人不会有很多店，只有一个」—— 不用为多店留后路。 */
                label="我是卖家"
                onClick={() => setIdentity(String(o.id))}
              />
            ))}
            {/*
              🔴 **第三个胶囊：平台后台。** 老板的原话：「陈总自己可以逛装修商店…
                 他也可以有自己的店…，管理平台。**这是三个东西，不要混在一起**」。
                 早先②的菜单里同时挂着「进货」（那是①）和「平台后台」（那是③）。
              ⚠️ 这一个是**跳走**不是切身份 —— 后台是另一套界面。
            */}
            {orgs.some((o) => o.type === "platform") && (
              <Link
                to="/admin"
                className="flex h-9 shrink-0 items-center gap-1.5 rounded-full bg-white/15 px-4 text-[13px] whitespace-nowrap text-white active:bg-white/25"
              >
                <Monitor className="size-3.5" />
                平台管理
              </Link>
            )}
          </div>
        )}
      </header>

      <div className="space-y-3 px-3 pb-6">
        {/*
          🔴 **整页按身份换内容，不是"买家内容 + 商家内容"堆在一起。**
             堆在一起的话页面会长到划不完，而他每次来其实只关心一种身份的事。
        */}
        {activeOrg ? (
          <MerchantView org={activeOrg} />
        ) : (
          <>
        {/*
          🔴 **「我自己」= 我这个人要买东西。**
             价格按我的进货价算（陈总没有上级，他的成本就是供应商的供货价）——
             所以这条入口在这儿，而不是挂在店铺菜单里叫「进货」。
          ⚠️ 只对有店的人显示：纯买家点「首页」就是逛商城，
             再摆一条"去逛商城"只会多一层。
        */}
        {orgs.length > 0 && (
          <Card className="-mt-6 overflow-hidden">
            {/* ⚠️ 不再标「按你的进货价」—— 商品页顶部那条横幅已经说过一次。 */}
            <MenuRow to="/restock" icon={PackagePlus} label="去逛商城" last />
          </Card>
        )}

        {/*
          🔴 订单四态直达。
          ⚠️ `-mt-6` 只能有一块用 —— 上面那条「去逛商城」有店的人才出现，
             两块都往上提的话会叠在一起。所以这里按有没有店来决定。
        */}
        <Card className={cn("px-1 py-3", orgs.length === 0 && "-mt-6")}>
          <div className="grid grid-cols-4">
            {ORDER_TABS.map((t) => (
              <Link
                key={t.key}
                to="/orders"
                search={{ status: t.key } as never}
                className="flex flex-col items-center gap-1.5 rounded-lg py-1.5 active:bg-secondary"
              >
                <t.icon className="size-[22px] text-brand-600" />
                <span className="text-[12px] text-muted-foreground">{t.label}</span>
              </Link>
            ))}
          </div>
        </Card>

        <div>
          <SectionTitle>常用</SectionTitle>
          <Card className="overflow-hidden">
            <MenuRow to="/orders" icon={Package} label="全部订单" />
            <MenuRow to="/addresses" icon={MapPin} label="收货地址" />
            {/* 🔴 这个平台上的账号是有钱的，登录设备管理是刚需不是锦上添花 */}
            <MenuRow to="/security" icon={ShieldCheck} label="账号安全" hint="登录设备" last />
          </Card>
        </div>
          </>
        )}

        <Button
          variant="outline"
          className="mt-4 w-full"
          loading={logout.isPending}
          onClick={async () => {
            await logout.mutateAsync();
            /**
             * ⚠️ **退出时把身份一起清掉。**
             *    不清的话，下一个人在这台手机上登录，会带着上一个人的
             *    采购身份逛商城 —— 价格是别人的进货价。
             */
            setIdentity("");
            writePurchaseAs(null);
            /**
             * ⚠️ **退出之后落到登录页，不是首页。**
             *    退出登录这个动作的下一步几乎总是"换个号登进来"或者"我不想让别人
             *    用我的号" —— 甩回首页等于让他自己再去找登录入口，
             *    而首页上那个入口在网页版顶栏最右边、手机版藏在「我的」里。
             */
            navigate({ to: "/login" });
          }}
        >
          <LogOut className="size-4" />
          退出登录
        </Button>
      </div>
    </TabPage>
  );
}

/** 手机版 / 网页版是两套页面，按视口挑一套（URL 与数据 hooks 两边共用）。 */
function MeRoute() {
  return useIsDesktop() ? <DesktopMe /> : <Me />;
}

export const meRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/me",
  component: MeRoute,
});
